/**
 * UpdateUserPreferencesCommand - Updates current user's preferences
 * Follows MediatR pattern (Command + Handler in same file)
 */

import type { ICommand, ICommandHandler, IHttpClient, BaseApiResponse } from '@/application/core';
import { toCamelCase } from '@/utils/caseTransform';
import type { UserPreferences } from '@/application/queries/userPreferences';

// ============================================================================
// Command (Request)
// ============================================================================

export class UpdateUserPreferencesCommand implements ICommand<UserPreferences> {
  readonly type = 'UpdateUserPreferencesCommand' as const;

  constructor(public readonly preferences: Partial<UserPreferences>) {}
}

// ============================================================================
// Handler
// ============================================================================

export class UpdateUserPreferencesCommandHandler
  implements ICommandHandler<UpdateUserPreferencesCommand, UserPreferences>
{
  constructor(private readonly httpClient: IHttpClient) {}

  async handle(command: UpdateUserPreferencesCommand): Promise<UserPreferences> {
    const { preferences } = command;

    // Backend expects snake_case per api-patterns.md
    const response = await this.httpClient.put<BaseApiResponse<any>>(
      '/api/v1/user/preferences',
      {
        language: preferences.language,
        timezone: preferences.timezone,
        timezone_offset: preferences.timezoneOffset,
        date_format: preferences.dateFormat,
        landing_page_controller: preferences.landingPageController,
        landing_page_action: preferences.landingPageAction,
        email_optout: preferences.emailOptout,
        ui_mode: preferences.uiMode,
      }
    );

    if (!response.succeeded) {
      throw new Error(response.message || 'Failed to update preferences');
    }

    return toCamelCase(response.data ?? {});
  }
}
